import type { LucideIcon } from 'lucide-react'
import {
  BarChart3,
  CalendarCheck,
  ClipboardList,
  Database,
  Download,
  FileText,
  House,
  Inbox,
  LayoutDashboard,
  PieChart,
  Plus,
  Settings,
  TriangleAlert,
  Wallet,
} from 'lucide-react'
import { es } from '@/textos/es'

export type Rol = keyof typeof es.rol

/** Roles que reciben dinero desde la base (R-15). La UI lo usa solo para no pedirlo. */
export const ROLES_CON_DINERO: readonly Rol[] = ['admin', 'gerencia', 'cobranza']
export const ROLES_OFICINA: readonly Rol[] = ['admin', 'gerencia', 'cobranza', 'operaciones']

export const veDinero = (rol: Rol | undefined) => !!rol && ROLES_CON_DINERO.includes(rol)
/** Lo mismo que deciden `app.edita_catalogos()` y `app.edita_cobranza()` en la base. */
export const editaCatalogos = (rol: Rol | undefined) => rol === 'admin' || rol === 'operaciones'
export const editaCobranza = (rol: Rol | undefined) => rol === 'admin' || rol === 'cobranza'
/** Lo mismo que decide `app.cancela_recibos()`: solo Admin y Gerencia cancelan Recibos (Poncho, 15 oct). */
export const cancelaRecibos = (rol: Rol | undefined) => rol === 'admin' || rol === 'gerencia'
/** Pagos (#25): registran y aplican Admin y Cobranza (`app.edita_cobranza()`); revierten Admin y
 * Gerencia, como cancelar un Recibo (`app.cancela_recibos()`). La base lo decide de todos modos. */
export const registraPagos = editaCobranza
export const reviertePagos = cancelaRecibos

/** Superficie de inicio de cada rol después del login. */
export function superficieDe(rol: Rol): string {
  if (ROLES_OFICINA.includes(rol)) return '/ops'
  if (rol === 'muelle') return '/muelle'
  if (rol === 'operador') return '/op'
  return '/portal'
}

export interface ItemNav {
  etiqueta: string
  ruta: string
  icono: LucideIcon
  roles: readonly Rol[]
}
export interface SeccionNav {
  titulo: string
  items: ItemNav[]
}

const OPS: readonly Rol[] = ['admin', 'operaciones', 'gerencia']
const DINERO: readonly Rol[] = ['admin', 'cobranza', 'gerencia']

const NAV: SeccionNav[] = [
  {
    titulo: es.nav.operacion,
    items: [
      // Inicio es la entrada de toda la oficina (D2, #57); cada rol ve ahí lo que le toca.
      { etiqueta: es.nav.inicio, ruta: '/ops', icono: House, roles: ROLES_OFICINA },
      { etiqueta: es.nav.tablero, ruta: '/ops/tablero', icono: LayoutDashboard, roles: OPS },
      { etiqueta: es.nav.ordenes, ruta: '/ops/ordenes', icono: ClipboardList, roles: OPS },
      {
        etiqueta: es.nav.porConfirmar,
        ruta: '/ops/por-confirmar',
        icono: Inbox,
        roles: ['admin', 'operaciones'],
      },
      {
        etiqueta: es.nav.cierre,
        ruta: '/ops/cierre',
        icono: CalendarCheck,
        roles: ['admin', 'operaciones'],
      },
      { etiqueta: es.nav.incidentes, ruta: '/ops/incidentes', icono: TriangleAlert, roles: OPS },
    ],
  },
  {
    titulo: es.nav.liquidacion,
    items: [
      {
        etiqueta: es.nav.nuevoRecibo,
        ruta: '/ops/recibos/nuevo',
        icono: Plus,
        roles: ['admin', 'cobranza'],
      },
      { etiqueta: es.nav.recibos, ruta: '/ops/recibos', icono: FileText, roles: DINERO },
      { etiqueta: es.nav.pagos, ruta: '/ops/pagos', icono: Wallet, roles: DINERO },
      { etiqueta: es.nav.cartera, ruta: '/ops/cartera', icono: PieChart, roles: DINERO },
      {
        etiqueta: es.nav.contpaqi,
        ruta: '/ops/contpaqi',
        icono: Download,
        roles: ['admin', 'cobranza'],
      },
    ],
  },
  {
    titulo: es.nav.administracion,
    items: [
      // Reportes: Operaciones ve solo los operativos (lo decide la pantalla de reportes).
      {
        etiqueta: es.nav.reportes,
        ruta: '/ops/reportes',
        icono: BarChart3,
        roles: ['admin', 'gerencia', 'operaciones'],
      },
      {
        etiqueta: es.nav.catalogos,
        ruta: '/ops/catalogos',
        icono: Database,
        roles: ROLES_OFICINA,
      },
      {
        etiqueta: es.nav.configuracion,
        ruta: '/ops/configuracion',
        icono: Settings,
        roles: ['admin'],
      },
    ],
  },
]

/** Navegación de oficina para un rol. Sin rol, vacía. */
export function navegacionPara(rol: Rol | undefined): SeccionNav[] {
  if (!rol) return []
  return NAV.map((s) => ({ ...s, items: s.items.filter((i) => i.roles.includes(rol)) })).filter(
    (s) => s.items.length > 0,
  )
}
